import { useEffect, useReducer, useState, useLayoutEffect, useMemo } from 'react';
import BaseDialog from '@/components/ui/BaseDialog';
import { Box, Chip, CircularProgress, Tab, Tabs, Typography } from '@mui/material';
import { HolidayVillage, Storefront } from '@mui/icons-material';
import BaseTransferList from '@/components/ui/BaseTransferList';
import BaseAutocomplete from '@/components/form/BaseAutocomplete';
import { Store } from '@/types';
import localforage from 'localforage';
import { fetchStoreListAPI } from '@/api/stores';
import { fetchClusterListAPI, fetchClusterDetailAPI } from '@/api/admin/rbac';
import { BaseCluster } from '@/types/pages/admin/cluster';
import { checkLayoutPackageStoresAPI, applyLayoutPackageAPI } from '@/api/layout';
import { Trans, useTranslation } from 'react-i18next';
import { useAppContext } from '@/provider/contexts';

interface BulkAssignDialogProps {
    isOpen: boolean;
    onConfirm: () => void;
    onClose: () => void;
    layoutPackageId: number;
    versionNumber?: number;
}

type State = {
    currentTab: 'cluster' | 'store';
    isClusterListLoading: boolean;
    clusterList: BaseCluster[];
    leftStoreList: Store[];
    rightStoreList: Store[];
    isStoreTransferListLoading: boolean;
};

type Action =
    | { type: 'SET_CURRENT_TAB'; payload: 'cluster' | 'store' }
    | { type: 'SET_IS_CLUSTER_LIST_LOADING'; payload: boolean }
    | { type: 'SET_CLUSTER_LIST'; payload: BaseCluster[] }
    | { type: 'SET_IS_STORE_TRANSFER_LIST_LOADING'; payload: boolean }
    | { type: 'SET_LEFT_STORE_LIST'; payload: Store[] }
    | { type: 'SET_RIGHT_STORE_LIST'; payload: Store[] };

function reducer(state: State, action: Action): State {
    switch (action.type) {
        case 'SET_CURRENT_TAB':
            return { ...state, currentTab: action.payload };
        case 'SET_IS_CLUSTER_LIST_LOADING':
            return { ...state, isClusterListLoading: action.payload };
        case 'SET_CLUSTER_LIST':
            return { ...state, clusterList: action.payload, isClusterListLoading: false };
        case 'SET_IS_STORE_TRANSFER_LIST_LOADING':
            return { ...state, isStoreTransferListLoading: action.payload };
        case 'SET_LEFT_STORE_LIST':
            return { ...state, leftStoreList: action.payload };
        case 'SET_RIGHT_STORE_LIST':
            return { ...state, rightStoreList: action.payload };
        default:
            return state;
    }
}

export default function BulkAssignDialog({
    isOpen,
    onConfirm,
    onClose,
    layoutPackageId,
    versionNumber,
}: BulkAssignDialogProps) {
    const [state, dispatch] = useReducer(reducer, {
        currentTab: 'cluster',
        isClusterListLoading: false,
        clusterList: [],
        leftStoreList: [],
        rightStoreList: [],
        isStoreTransferListLoading: false,
    });
    const { t } = useTranslation('common');
    const { setNotification } = useAppContext();
    const { currentTab, clusterList, leftStoreList, rightStoreList, isClusterListLoading, isStoreTransferListLoading } =
        state;

    const [allStores, setAllStores] = useState<Store[]>([]);
    const [selectedClusters, setSelectedClusters] = useState<BaseCluster[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [showOverwriteDlg, setShowOverwriteDlg] = useState(false);
    const [conflictStores, setConflictStores] = useState<Store[]>([]);
    const [layoutToken, setLayoutToken] = useState<string>(''); // <-- string, not SetStateAction

    const bulkAssignValid = useMemo(() => rightStoreList.length > 0, [rightStoreList]);
    // --- load clusters ---
    useEffect(() => {
        if (!isOpen) return;
        dispatch({ type: 'SET_IS_CLUSTER_LIST_LOADING', payload: true });
        (async () => {
            try {
                const cid = (await localforage.getItem('corporation_id')) as number;
                const {
                    data: {
                        data: { aaData },
                    },
                } = await fetchClusterListAPI({
                    cid,
                    data_per_page: 1000,
                    current_page: 1,
                    f_cluster_name: '',
                });
                dispatch({ type: 'SET_CLUSTER_LIST', payload: aaData || [] });
            } catch {
                dispatch({ type: 'SET_CLUSTER_LIST', payload: [] });
            }
        })();
    }, [isOpen]);

    useLayoutEffect(() => {
        if (!isOpen) return;
        dispatch({ type: 'SET_LEFT_STORE_LIST', payload: [] });
        dispatch({ type: 'SET_RIGHT_STORE_LIST', payload: [] });
        dispatch({ type: 'SET_IS_STORE_TRANSFER_LIST_LOADING', payload: true });
    }, [isOpen, layoutPackageId]);

    // --- load all stores ---
    useEffect(() => {
        if (!isOpen) return;
        dispatch({ type: 'SET_IS_STORE_TRANSFER_LIST_LOADING', payload: true });
        (async () => {
            try {
                const cid = (await localforage.getItem('corporation_id')) as number;
                const {
                    data: { data, isSuccess },
                } = await fetchStoreListAPI({ cid });
                if (isSuccess) {
                    const stores = data.aaData || [];
                    const assignedStores: Store[] = [];
                    const unassignedStores: Store[] = [];
                    stores.forEach((s) => {
                        const hasPackage = String(s.layout_package_id) === String(layoutPackageId);
                        if (hasPackage) {
                            assignedStores.push(s);
                            return;
                        }
                        unassignedStores.push(s);
                    });

                    setAllStores(stores);
                    dispatch({ type: 'SET_LEFT_STORE_LIST', payload: unassignedStores });
                    dispatch({ type: 'SET_RIGHT_STORE_LIST', payload: assignedStores });
                    return;
                }
                throw new Error();
            } catch {
                setAllStores([]);
                dispatch({ type: 'SET_LEFT_STORE_LIST', payload: [] });
                dispatch({ type: 'SET_RIGHT_STORE_LIST', payload: [] });
            } finally {
                dispatch({ type: 'SET_IS_STORE_TRANSFER_LIST_LOADING', payload: false });
            }
        })();
    }, [isOpen, layoutPackageId]);

    // --- filter stores by tab/cluster ---
    useEffect(() => {
        if (!isOpen) return;

        if (currentTab === 'store' || selectedClusters.length === 0) {
            const rightIds = new Set(rightStoreList.map((s) => s.store_id));
            const leftCandidates = allStores.filter((s) => !rightIds.has(s.store_id));
            dispatch({ type: 'SET_LEFT_STORE_LIST', payload: leftCandidates });
            return;
        }

        (async () => {
            dispatch({ type: 'SET_IS_STORE_TRANSFER_LIST_LOADING', payload: true });
            try {
                const cid = (await localforage.getItem('corporation_id')) as number;
                const clusters = await Promise.all(
                    selectedClusters.map((c) => fetchClusterDetailAPI(c.id, { cid }).then((r) => r.data.data)),
                );
                const allowed = new Set<number>(
                    clusters.flatMap((cl) => cl.stores.map((s) => (s as any).store_id ?? (s as any).id)),
                );
                const rightIds = new Set(rightStoreList.map((s) => s.store_id));
                const leftCandidates = allStores.filter((s) => allowed.has(s.store_id) && !rightIds.has(s.store_id));
                dispatch({ type: 'SET_LEFT_STORE_LIST', payload: leftCandidates });
            } catch {
                dispatch({ type: 'SET_LEFT_STORE_LIST', payload: [] });
            } finally {
                dispatch({ type: 'SET_IS_STORE_TRANSFER_LIST_LOADING', payload: false });
            }
        })();
    }, [isOpen, currentTab, selectedClusters, allStores]);

    // --- helper that *always* takes a string token ---
    const applyWithToken = async (token: string) => {
        setIsSaving(true);
        try {
            const cid = (await localforage.getItem('corporation_id')) as number;
            await applyLayoutPackageAPI(cid, { layout_token: token });
            onConfirm();
            onClose();
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveClick = async () => {
        setIsSaving(true);
        try {
            const cid = (await localforage.getItem('corporation_id')) as number;
            const payload = {
                layout_package_id: layoutPackageId,
                version_number: versionNumber,
                assigned_stores: rightStoreList.map((s) => s.store_id),
            };
            const {
                data: { data, isSuccess, message },
            } = await checkLayoutPackageStoresAPI(cid, payload);
            if (isSuccess) {
                const { layout_token, stores_with_existing_lp } = data || {};
                setLayoutToken(layout_token);
                const newToken = layout_token ?? '';

                const conflicts: number[] = stores_with_existing_lp ?? [];
                if (conflicts.length > 0) {
                    setConflictStores(allStores.filter((s) => conflicts.includes(s.store_id)));
                    setShowOverwriteDlg(true);
                    return;
                }

                await applyWithToken(newToken);
                return;
            }
            throw new Error(message);
        } catch (error: unknown) {
            if (error instanceof Error) {
                setNotification({ message: error?.message || t('Failed to Save'), severity: 'error' });
                return;
            }
            console.error('Error saving message:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const ClustersAutoComplete = () => (
        <BaseAutocomplete<BaseCluster, true, false, false>
            multiple
            label="Cluster"
            size="small"
            loading={isClusterListLoading}
            options={clusterList}
            value={selectedClusters}
            onChange={(_, next) => setSelectedClusters(next)}
            getOptionLabel={(opt) => opt.cluster_name}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            renderTags={(tags, getTagProps) =>
                tags.map((tag, idx) => {
                    const { key, ...rest } = getTagProps({ index: idx });
                    return <Chip key={key} label={tag.cluster_name} {...rest} />;
                })
            }
        />
    );

    return (
        <>
            <BaseDialog
                open={isOpen}
                maxWidth="md"
                dialogTitle={t('Bulk Assign')}
                disableConfirm={!bulkAssignValid}
                showSpinnerOnDisable={false}
                confirmButtonText={isSaving ? <CircularProgress size={20} /> : t('Save')}
                cancelButtonText={t('Cancel')}
                onConfirm={handleSaveClick}
                onClose={onClose}
            >
                <Box sx={{ width: '100%' }}>
                    <Tabs
                        value={currentTab}
                        onChange={(_, v) => dispatch({ type: 'SET_CURRENT_TAB', payload: v })}
                        sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 35 }}
                    >
                        <Tab icon={<HolidayVillage />} iconPosition="start" label={t('Cluster')} value="cluster" />
                        <Tab icon={<Storefront />} iconPosition="start" label={t('Store')} value="store" />
                    </Tabs>

                    {currentTab === 'cluster' && (
                        <Box sx={{ mt: 2, mb: 1 }}>
                            <ClustersAutoComplete />
                        </Box>
                    )}

                    <BaseTransferList
                        leftList={leftStoreList}
                        rightList={rightStoreList}
                        idKey="store_id"
                        isLoading={isStoreTransferListLoading}
                        labelKey="store_name"
                        listsTitle={{
                            left: currentTab === 'cluster' ? t('Filtered Stores') : t('All Stores'),
                            right: t('Package Stores'),
                        }}
                        listHeight={450}
                        onUpdateLists={(l, r) => {
                            dispatch({ type: 'SET_LEFT_STORE_LIST', payload: l });
                            dispatch({ type: 'SET_RIGHT_STORE_LIST', payload: r });
                        }}
                    />
                </Box>
            </BaseDialog>

            <BaseDialog
                open={showOverwriteDlg}
                dialogTitle={t('Overwrite Existing Layouts?')}
                confirmButtonText={isSaving ? <CircularProgress size={20} /> : t('Overwrite')}
                cancelButtonText={t('Cancel')}
                onConfirm={async () => {
                    setShowOverwriteDlg(false);
                    await applyWithToken(layoutToken); // <-- layoutToken is a string
                }}
                onClose={() => setShowOverwriteDlg(false)}
            >
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: 1,
                        mt: 1,
                    }}
                >
                    <Typography>
                        <Trans
                            i18nKey="storesWithExistingLayouts"
                            values={{ count: conflictStores.length }}
                            components={{ strong: <strong /> }}
                        />
                    </Typography>
                    <Box
                        component="ul"
                        sx={{
                            listStylePosition: 'inside',
                            pl: 2,
                            m: 0,
                            width: '100%',
                            textAlign: 'left',
                        }}
                    >
                        {conflictStores.slice(0, 5).map((s) => (
                            <li key={s.store_id}>{s.store_name}</li>
                        ))}
                        {conflictStores.length > 5 && <li>{t('andMoreN', { count: conflictStores.length - 5 })}</li>}
                    </Box>
                </Box>
            </BaseDialog>
        </>
    );
}
